import { useState, useEffect, useCallback, useRef } from 'react';
import { getUnreadNudges, markNudgeRead } from './api.js';
import { uniqueNudges, sortNudgesByDate } from './utils.js';
import { useAuth } from '../../contexts/AuthContext';
import { useStudentNotifications } from '../../hooks/useStudentNotifications.js';

export const useUnreadNudges = (options = {}) => {
  const { autoFetch = true } = options;
  const { user, isAuthenticated } = useAuth();
  
  const [nudges, setNudges] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  
  const mountedRef = useRef(true);
  const initialFetchDoneRef = useRef(false);
  
  // Fetch unread nudges (for initial load and manual refresh)
  const fetchNudges = useCallback(async () => {
    if (!mountedRef.current) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const fetchedNudges = await getUnreadNudges();
      const uniqueAndSorted = sortNudgesByDate(uniqueNudges(fetchedNudges));
      
      // Always set the state
      setNudges(uniqueAndSorted);
      
      // Reset current index if we have new nudges
      setCurrentIndex(prev => {
        if (prev >= uniqueAndSorted.length) {
          return Math.max(0, uniqueAndSorted.length - 1);
        }
        return prev;
      });
    } catch (err) {
      console.error('[NUDGES] Error fetching nudges:', err);
      if (mountedRef.current) {
        setError(err instanceof Error ? err.message : 'Failed to fetch nudges');
      }
    } finally {
      if (mountedRef.current) {
        setLoading(false);
      }
    }
  }, []);
  
  // Handle new message received via WebSocket
  const handleWebSocketMessage = useCallback((message) => {
    if (!message || !message.id) {
      console.warn('[NUDGES] Invalid message format:', message);
      return;
    }
    
    // Add the new message to the nudges list
    setNudges(prev => {
      // Check if message already exists (avoid duplicates)
      const exists = prev.some(n => n && n.id === message.id);
      if (exists) {
        return prev;
      }
      
      // Add new message and sort
      const updated = [...prev, message];
      const sorted = sortNudgesByDate(uniqueNudges(updated));
      return sorted;
    });
  }, []);
  
  // Setup WebSocket connection for real-time notifications
  const authStatus = isAuthenticated();
  const isStudent = user?.is_student && user?.id;
  const wsEnabled = authStatus && isStudent;
  
  useStudentNotifications(handleWebSocketMessage, {
    enabled: wsEnabled,
    reconnectInterval: 5000,
  });
  
  // Initial fetch when user becomes available
  useEffect(() => {
    const authStatus = isAuthenticated();
    
    if (autoFetch && authStatus && user?.is_student && user?.id && !initialFetchDoneRef.current) {
      initialFetchDoneRef.current = true;
      fetchNudges();
    }
  }, [fetchNudges, autoFetch, user?.id, user?.is_student]);
  
  // Mark a nudge as read
  const markRead = useCallback(async (id) => {
    try {
      await markNudgeRead(id);
      
      // Remove the nudge from the list
      setNudges(prev => prev.filter(nudge => nudge.id !== id));
      
      // Adjust current index if needed
      setCurrentIndex(prev => {
        const newLength = nudges.length - 1;
        if (prev >= newLength && newLength > 0) {
          return Math.max(0, newLength - 1);
        }
        return prev;
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to mark nudge as read');
    }
  }, [nudges.length]);
  
  // Move to next nudge
  const nextNudge = useCallback(() => {
    setCurrentIndex(prev => {
      if (prev < nudges.length - 1) {
        return prev + 1;
      }
      return prev; // Stay at the last nudge
    });
  }, [nudges.length]);
  
  // Cleanup on unmount
  useEffect(() => {
    return () => {
      mountedRef.current = false;
    };
  }, []);
  
  // Get current nudge
  const currentNudge = nudges[currentIndex] || null;
  
  return {
    nudges,
    loading,
    error,
    refresh: fetchNudges,
    markRead,
    currentNudge,
    nextNudge,
  };
};
